import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Check, Settings as SettingsIcon } from "lucide-react";

// Bump this version to force banner re-display for all users (e.g. policy changes)
const STORAGE_KEY = "ashir_cookie_consent_v2";

const defaultPrefs = {
  essential: true, // sempre true, não pode desligar
  analytics: true,
  marketing: true,
  acceptedAt: null,
};

export default function CookieConsent() {
  // Show banner by default; effect will hide it only if valid consent exists in localStorage.
  // This guarantees the banner appears for every user without prior consent, even if
  // localStorage is unavailable, blocked, or contains malformed data.
  const [visible, setVisible] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.acceptedAt) {
          setPrefs(parsed);
          setVisible(false);
          return;
        }
      }
      // No valid consent found — keep banner visible
      setVisible(true);
    } catch (err) {
      // Any error reading storage → still show banner
      // eslint-disable-next-line no-console
      console.warn("[CookieConsent] localStorage read failed:", err);
      setVisible(true);
    }
  }, []);

  const save = (p) => {
    const payload = { ...p, essential: true, acceptedAt: new Date().toISOString() };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[CookieConsent] localStorage write failed:", err);
    }
    setPrefs(payload);
    setVisible(false);
    setShowSettings(false);
  };

  const acceptAll = () =>
    save({ essential: true, analytics: true, marketing: true });

  const rejectNonEssential = () =>
    save({ essential: true, analytics: false, marketing: false });

  if (!visible) return null;

  return (
    <>
      {/* Banner */}
      {!showSettings && (
        <div
          data-testid="cookie-banner"
          role="dialog"
          aria-live="polite"
          className="fixed z-[60] bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-2xl md:bottom-6 animate-[ag-fade-up_0.5s_ease-out]"
        >
          <div className="relative bg-[#05070D]/95 backdrop-blur-2xl border border-[#D4AF37]/40 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] p-6 md:p-7">
            <div className="flex items-start gap-4">
              <div className="hidden md:flex w-11 h-11 border border-[#D4AF37]/40 items-center justify-center shrink-0">
                <Cookie size={20} strokeWidth={1.4} className="text-[#D4AF37]" />
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <Cookie size={14} strokeWidth={1.6} className="text-[#D4AF37] md:hidden" />
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] font-semibold">
                    Aviso de Cookies · LGPD
                  </span>
                </div>

                <p className="text-[#D6DBE2] text-sm md:text-[15px] font-light leading-relaxed">
                  Utilizamos cookies para melhorar sua experiência e analisar o
                  tráfego do site. Ao continuar navegando, você concorda com
                  nossa{" "}
                  <Link
                    to="/privacidade"
                    data-testid="cookie-privacy-link"
                    className="text-[#D4AF37] underline underline-offset-4 hover:text-[#F0CC59]"
                  >
                    Política de Privacidade
                  </Link>
                  .
                </p>

                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                  <button
                    data-testid="cookie-accept-all"
                    onClick={acceptAll}
                    className="group inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#05070D] px-6 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-[#F0CC59] transition-colors"
                  >
                    <Check size={14} strokeWidth={2} />
                    Aceitar Tudo
                  </button>
                  <button
                    data-testid="cookie-settings-open"
                    onClick={() => setShowSettings(true)}
                    className="inline-flex items-center justify-center gap-2 border border-white/15 text-[#D6DBE2] px-6 py-3 text-[11px] tracking-[0.22em] uppercase font-light hover:border-[#D4AF37]/60 hover:text-white transition-colors"
                  >
                    <SettingsIcon size={13} strokeWidth={1.6} />
                    Configurações
                  </button>
                </div>
              </div>

              <button
                onClick={rejectNonEssential}
                data-testid="cookie-close"
                aria-label="Fechar (aceitar apenas essenciais)"
                className="text-[#A3ABB8] hover:text-white shrink-0"
              >
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings modal */}
      {showSettings && (
        <div
          data-testid="cookie-settings-modal"
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-[ag-fade-up_0.35s_ease-out]"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowSettings(false);
          }}
        >
          <div className="w-full max-w-lg bg-[#05070D] border border-[#D4AF37]/40 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
              <div className="flex items-center gap-3">
                <Cookie size={18} strokeWidth={1.4} className="text-[#D4AF37]" />
                <span className="overline">Preferências de Cookies</span>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                data-testid="cookie-settings-close"
                aria-label="Fechar configurações"
                className="text-[#A3ABB8] hover:text-white"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            <div className="px-6 py-6 space-y-5 max-h-[60vh] overflow-y-auto">
              <ToggleRow
                testid="pref-essential"
                title="Essenciais"
                desc="Necessários para o funcionamento básico do site. Não podem ser desativados."
                checked
                disabled
              />
              <ToggleRow
                testid="pref-analytics"
                title="Análise de Tráfego"
                desc="Estatísticas anônimas de navegação para melhorar a experiência do site."
                checked={prefs.analytics}
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              />
              <ToggleRow
                testid="pref-marketing"
                title="Marketing"
                desc="Permite personalização de campanhas e mensuração de conversões."
                checked={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
              />
            </div>

            <div className="px-6 py-5 border-t border-white/5 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="cookie-reject-all"
                onClick={rejectNonEssential}
                className="flex-1 border border-white/15 text-[#D6DBE2] px-5 py-3 text-[11px] tracking-[0.22em] uppercase font-light hover:border-[#D4AF37]/60 hover:text-white transition-colors"
              >
                Recusar não essenciais
              </button>
              <button
                data-testid="cookie-save-prefs"
                onClick={() => save(prefs)}
                className="flex-1 bg-[#D4AF37] text-[#05070D] px-5 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-[#F0CC59] transition-colors"
              >
                Salvar Preferências
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ToggleRow({ testid, title, desc, checked, disabled, onChange }) {
  return (
    <div className="flex items-start justify-between gap-6 border-b border-white/5 pb-5 last:border-0 last:pb-0">
      <div className="flex-1">
        <div className="text-white text-sm font-medium">{title}</div>
        <div className="text-[#A3ABB8] text-xs mt-1 font-light leading-relaxed">
          {desc}
        </div>
      </div>
      <button
        data-testid={testid}
        onClick={() => !disabled && onChange && onChange(!checked)}
        disabled={disabled}
        aria-pressed={checked}
        className={`relative w-11 h-6 shrink-0 transition-colors ${
          checked ? "bg-[#D4AF37]" : "bg-white/10"
        } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 bg-[#05070D] transition-all ${
            checked ? "left-[22px]" : "left-0.5"
          }`}
        />
      </button>
    </div>
  );
}
